import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@renderer/chat/providers'
import { useUnifiedBilling } from '@renderer/chat/store/global-billing'
import { useInvalidatePlanAllowances } from '@renderer/chat/hooks/use-plan-allowances'
import { useRecordBriefLimit } from '../../sidepanel/plan-banners/plan-banner-state'
import { v1Client } from '@renderer/chat/api/v1Client'
import { meetingBriefQueryKey, seedGeneratingBrief } from './meeting-brief-query'
import type { ApiBriefData } from './types'

async function postBrief(calendarEventId: string, token: string, regenerate = false): Promise<ApiBriefData> {
  // Swap the cast for a typed call once `chat/types/backend-openapi.d.ts`
  // is regenerated to include `/calendar-events/{id}/brief`.
  const response = (await (v1Client as any).POST('/calendar-events/{id}/brief', {
    params: { path: { id: calendarEventId } },
    headers: { Authorization: `Bearer ${token}` },
    body: { regenerate },
  })) as { error?: unknown; data?: { success: boolean; data: ApiBriefData } }
  if (response.error) {
    throw new Error(briefErrorMessage(response.error))
  }
  if (!response.data?.data) throw new Error('Brief generation response missing data')
  return response.data.data
}

/** The gateway's 403 for a plan whose monthly briefs are spent (MEETING_BRIEF_LIMIT_ERROR). */
const BRIEF_LIMIT_MESSAGE = 'meeting briefs for this billing period are used up'

/** A string error, or the `{ error }` body the brief route returns. */
function briefErrorMessage(error: unknown): string {
  if (typeof error === 'string') return error
  if (error && typeof error === 'object' && 'error' in error && typeof error.error === 'string') return error.error
  return 'Brief generation failed'
}

/**
 * Kicks off agentic brief generation for a calendar event. Idempotent
 * server-side — calling repeatedly with the same calendarEventId returns
 * the in-flight or already-ready conversationId without spawning a new run.
 */
export function useTriggerBriefGeneration() {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()
  const { subscription } = useUnifiedBilling()
  const recordBriefLimit = useRecordBriefLimit()
  const invalidateAllowances = useInvalidatePlanAllowances()

  return useMutation({
    mutationKey: ['trigger-meeting-brief'],
    mutationFn: async (input: string | { calendarEventId: string; regenerate?: boolean }) => {
      const calendarEventId = typeof input === 'string' ? input : input.calendarEventId
      const regenerate = typeof input === 'string' ? false : !!input.regenerate
      const token = await getAuthToken()
      if (!token) throw new Error('No highlight token found')
      return postBrief(calendarEventId, token, regenerate)
    },
    onError: (error) => {
      if (error.message.includes(BRIEF_LIMIT_MESSAGE)) {
        recordBriefLimit(subscription.currentPeriodEndAt ?? '')
      }
    },
    onSettled: () => invalidateAllowances(),
    onSuccess: async (data, input) => {
      const calendarEventId = typeof input === 'string' ? input : input.calendarEventId
      const queryKey = meetingBriefQueryKey(calendarEventId)
      if (data.status !== 'generating') {
        await queryClient.invalidateQueries({ queryKey })
        return
      }
      // Don't let an in-flight GET (possibly a cached 404) resolve over the seed.
      await queryClient.cancelQueries({ queryKey })
      queryClient.setQueryData<ApiBriefData | null>(queryKey, (current) =>
        seedGeneratingBrief(current, data.conversationId),
      )
    },
  })
}
